import { useSelectorApp, useDispatchApp } from "../../lib/redux";
import { useNotifications } from "../../hooks/useNotifications";
import { useNotificationsToggle } from "../../hooks/useNotificationsToggle";
import { useCreativeJobsListener } from "../../hooks/useCreativeJobsListener";
import { useNotificationsFabPosition } from "../../hooks/useNotificationsFabPosition";
import { NotificationsPanel } from "./NotificationsPanel";
import NotificationsButton from "./NotificationsButton";
import {
  setNotificationsPanelIsExpanded,
  setNotificationsPanelIsClosed,
} from "../../actions/ui";
import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import {
  getOpenAlertModals,
  closeAlertModal,
} from "../../utils/alertModalsState";
import useOutsideCampaignGallery from "../../hooks/useOutsideCampaignGallery";
import ModalBulkCampaigns from "../Modal/ModalBulkCampaigns/ModalBulkCampaigns";
import ModalCustomReport from "../Modal/ModalCustomReport";
import ModalImageEdit from "../Modal/ModalImageEdit";
import { hasAlertsChanged } from "../../utils/notifications";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { getPathDatabaseByRole } from "../../actions/auth";
import AlertCreativeJobModals from "./AlertCreativeJobModals";
import { getCreativeJobAlertConfig } from "../../utils/creativeJobAlertConfig";
import {
  startAddContentsToList,
  startBumpAndUpdateContentInList,
  startBumpContentInList,
  startUpdateCarouselImageUrl,
} from "../../actions/user";
import { ALERTS_ASYNC_STATUS, OWNER_ROLE } from "../../utils/constants";
import { useTranslationApp } from "../../lib/i18next";
import { MODAL_CREATE_WITH_IA_YOU_CAN_LEAVE_THE_PAGE } from "../../i18n/keysTranslations";

const NOTIFICATIONS_OVERLAY_Z_INDEX = 2147483647;

// ConfirmAlert renders in a SweetAlert container on document.body, above this
// overlay. The path is captured at dispatch, so Cancel, Confirm, and the
// backdrop still count as inside the dialog after it starts closing.
const isClickInsidePortaledAlert = (event) => {
  const path =
    typeof event?.composedPath === "function"
      ? event.composedPath()
      : [event?.target];

  return path.some(
    (node) =>
      node instanceof Element && node.classList.contains("swal2-container")
  );
};

const GlobalNotificationsPanel = () => {
  const { t } = useTranslationApp();
  const { initialSyncDone } = useCreativeJobsListener();
  const { onToggle, hasActiveProcessing } = useNotificationsToggle();
  const buttonRef = useRef(null);
  const user = useSelectorApp((state) => state.auth.user);
  const isOwner = user?.role === OWNER_ROLE;
  const isPlanInactive = useSelectorApp(
    (state) =>
      state.payments.isPlanActiveResolved && !state.payments.isPlanActive
  );
  const isMobileViewport = useMediaQuery((theme) =>
    theme.breakpoints.down("md")
  );
  const dispatch = useDispatchApp();
  const notificationsPanelIsExpanded = useSelectorApp(
    (state) => state.alerts?.notificationsPanelIsExpanded || false
  );
  const notificationsPanelIsClosed = useSelectorApp(
    (state) => state.alerts?.notificationsPanelIsClosed || false
  );
  const notifications = useSelectorApp(
    (state) => state.alerts?.notifications || []
  );
  const { activeAlerts, onCloseAlert, onCloseAllAlerts } = useNotifications();
  const [openModals, setOpenModals] = useState([]);
  const previousAlertsRef = useRef([]);
  const panelRef = useRef(null);
  const wasManuallyClosedRef = useRef(false);
  const hasSeededBaselineRef = useRef(false);

  const { userID } = dispatch(getPathDatabaseByRole());
  const { onOpenGallery } = useOutsideCampaignGallery();

  const isModalCreateWithIAOpen = useSelectorApp(
    (state) => state.ui?.openModals?.isModalCreateWithIA || false
  );

  const hasGeneratingCreativeInProgress = activeAlerts.some(
    (alert) =>
      alert?.type === "generatingCreative" &&
      (alert?.status === ALERTS_ASYNC_STATUS.LOADING ||
        alert?.status === ALERTS_ASYNC_STATUS.GENERATING)
  );
  const hasCustomReportInProgress = activeAlerts.some(
    (alert) =>
      alert?.type === "generatingCustomReport" &&
      (alert?.status === ALERTS_ASYNC_STATUS.LOADING ||
        alert?.status === ALERTS_ASYNC_STATUS.GENERATING)
  );
  const hasAnalyzingTemplateInProgress = activeAlerts.some(
    (alert) =>
      alert?.type === "analyzingCreativeTemplate" &&
      (alert?.status === ALERTS_ASYNC_STATUS.LOADING ||
        alert?.status === ALERTS_ASYNC_STATUS.GENERATING)
  );
  const hasProducingTemplateInProgress = activeAlerts.some(
    (alert) =>
      alert?.type === "producingCreativeTemplate" &&
      (alert?.status === ALERTS_ASYNC_STATUS.LOADING ||
        alert?.status === ALERTS_ASYNC_STATUS.GENERATING)
  );
  const headerHint =
    (isModalCreateWithIAOpen && hasGeneratingCreativeInProgress) ||
    hasCustomReportInProgress ||
    hasAnalyzingTemplateInProgress ||
    hasProducingTemplateInProgress
      ? t(MODAL_CREATE_WITH_IA_YOU_CAN_LEAVE_THE_PAGE)
      : null;

  const onTogglePanel = useCallback(() => {
    if (isPlanInactive) {
      return;
    }

    if (notificationsPanelIsExpanded) {
      wasManuallyClosedRef.current = true;
      previousAlertsRef.current = notifications.map((alert) => ({ ...alert }));
    } else {
      wasManuallyClosedRef.current = false;
    }
    onToggle();
  }, [isPlanInactive, notifications, notificationsPanelIsExpanded, onToggle]);

  const { fabPosition, isDragging, dragHandlers } = useNotificationsFabPosition(
    {
      buttonRef,
      onClick: onTogglePanel,
    }
  );

  // Track when user manually closes panel
  useLayoutEffect(() => {
    if (notificationsPanelIsClosed && !notificationsPanelIsExpanded) {
      if (!wasManuallyClosedRef.current) {
        wasManuallyClosedRef.current = true;
      }
    } else if (notificationsPanelIsExpanded) {
      wasManuallyClosedRef.current = false;
    }
  }, [notificationsPanelIsClosed, notificationsPanelIsExpanded]);

  useEffect(() => {
    if (!isPlanInactive || !notificationsPanelIsExpanded) {
      return;
    }

    wasManuallyClosedRef.current = true;
    dispatch(setNotificationsPanelIsExpanded(false));
    dispatch(setNotificationsPanelIsClosed(true));
  }, [dispatch, isPlanInactive, notificationsPanelIsExpanded]);

  useEffect(() => {
    if (!initialSyncDone) {
      hasSeededBaselineRef.current = false;
      previousAlertsRef.current = [];
      return;
    }

    if (!hasSeededBaselineRef.current) {
      previousAlertsRef.current = notifications.map((alert) => ({ ...alert }));
      hasSeededBaselineRef.current = true;
      return;
    }

    const previousAlerts = previousAlertsRef.current;

    // Check if alerts have changed (new alerts or status changes)
    const alertsChanged = hasAlertsChanged(notifications, previousAlerts);

    // If panel is already open, keep it open to prevent flickering when alert status changes
    if (notificationsPanelIsExpanded) {
      previousAlertsRef.current = notifications.map((alert) => ({ ...alert }));
      if (wasManuallyClosedRef.current) {
        wasManuallyClosedRef.current = false;
      }
      return;
    }

    // If user closed the panel, still auto-open on new alerts or status changes
    if (wasManuallyClosedRef.current && !notificationsPanelIsExpanded) {
      if (alertsChanged && !isMobileViewport && !isPlanInactive) {
        dispatch(setNotificationsPanelIsExpanded(true));
        dispatch(setNotificationsPanelIsClosed(false));
        wasManuallyClosedRef.current = false;
        previousAlertsRef.current = notifications.map((alert) => ({
          ...alert,
        }));
      }
      // Don't update previousAlertsRef if panel stays closed - we need to keep the old state for comparison
      // This allows us to detect changes when alerts update later
      return;
    }

    // Auto-open panel if alerts changed and panel is not expanded (skipped on mobile)
    if (
      alertsChanged &&
      !notificationsPanelIsExpanded &&
      !isMobileViewport &&
      !isPlanInactive
    ) {
      dispatch(setNotificationsPanelIsExpanded(true));
      dispatch(setNotificationsPanelIsClosed(false));
      previousAlertsRef.current = notifications.map((alert) => ({ ...alert }));
    }
  }, [
    initialSyncDone,
    notifications,
    notificationsPanelIsExpanded,
    dispatch,
    isMobileViewport,
    isPlanInactive,
  ]);

  const onClose = useCallback(
    (event) => {
      event?.stopPropagation?.();
      wasManuallyClosedRef.current = true;
      previousAlertsRef.current = notifications.map((alert) => ({ ...alert }));
      dispatch(setNotificationsPanelIsExpanded(false));
      dispatch(setNotificationsPanelIsClosed(true));
    },
    [dispatch, notifications]
  );

  useEffect(() => {
    const onModalStateChange = () => {
      setOpenModals(getOpenAlertModals());
    };

    window.addEventListener("alertModalStateChanged", onModalStateChange);
    onModalStateChange();

    return () => {
      window.removeEventListener("alertModalStateChanged", onModalStateChange);
    };
  }, []);

  const onCloseModal = (alertId, originalOnClose) => {
    closeAlertModal(alertId);
    if (originalOnClose) {
      originalOnClose();
    }
  };

  const onSaveImageEditFromAlert = useCallback(
    async (savePayload, generationData) => {
      const editedUrl =
        typeof savePayload === "string" ? savePayload : savePayload?.url;
      const mode =
        typeof savePayload === "string"
          ? "replace"
          : savePayload?.mode || "replace";
      if (!editedUrl) return;

      if (mode === "new") {
        await dispatch(
          startAddContentsToList([{ url: editedUrl, from: "ai", userID: null }])
        );
        return;
      }

      const contentID = generationData?.contentID;
      if (!contentID) return;

      if (
        generationData?.isCarousel &&
        generationData?.carouselIndex !== undefined &&
        generationData?.carouselIndex !== null
      ) {
        await dispatch(
          startUpdateCarouselImageUrl({
            contentID,
            carouselIndex: generationData.carouselIndex,
            url: editedUrl,
          })
        );
        await dispatch(startBumpContentInList({ id: contentID }));
        return;
      }

      await dispatch(
        startBumpAndUpdateContentInList({ id: contentID, url: editedUrl })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    const onClickOutside = (event) => {
      if (isClickInsidePortaledAlert(event)) return;

      if (
        notificationsPanelIsExpanded &&
        panelRef.current &&
        !panelRef.current.contains(event.target)
      ) {
        const notificationsButton = document.getElementById(
          "notifications-toggle-button"
        );
        const isClickOnButton =
          (buttonRef.current &&
            (buttonRef.current === event.target ||
              buttonRef.current.contains(event.target))) ||
          (notificationsButton &&
            (notificationsButton.contains(event.target) ||
              notificationsButton === event.target ||
              event.target.closest("#notifications-toggle-button")));

        if (!isClickOnButton) {
          onClose();
        }
      }
    };

    if (notificationsPanelIsExpanded) {
      const timeoutId = setTimeout(() => {
        document.addEventListener("click", onClickOutside);
      }, 50);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("click", onClickOutside);
      };
    }
  }, [notificationsPanelIsExpanded, onClose]);

  const overlayContent =
    !isOwner && typeof document !== "undefined"
      ? createPortal(
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: NOTIFICATIONS_OVERLAY_Z_INDEX,
              pointerEvents: "none",
            }}
          >
            <Box
              ref={buttonRef}
              sx={{
                position: "fixed",
                bottom: fabPosition.bottom,
                right: fabPosition.right,
                pointerEvents: "auto",
                touchAction: "none",
                cursor: isPlanInactive
                  ? "default"
                  : isDragging
                    ? "grabbing"
                    : "grab",
                userSelect: "none",
              }}
              {...dragHandlers}
            >
              <NotificationsButton
                id="notifications-toggle-button"
                variant="fab"
                hasActiveProcessing={hasActiveProcessing}
                disabled={isPlanInactive}
                isDragging={isDragging}
                sx={{ pointerEvents: "none" }}
              />
            </Box>
            <Box
              ref={panelRef}
              sx={{
                pointerEvents: notificationsPanelIsExpanded ? "auto" : "none",
              }}
            >
              <NotificationsPanel
                isExpanded={notificationsPanelIsExpanded}
                activeAlerts={activeAlerts}
                onClose={onClose}
                onCloseAlert={onCloseAlert}
                onCloseAllAlerts={onCloseAllAlerts}
                zIndex={NOTIFICATIONS_OVERLAY_Z_INDEX}
                fabPosition={fabPosition}
                buttonRef={buttonRef}
                headerHint={headerHint}
              />
            </Box>
          </Box>,
          document.body
        )
      : null;

  return (
    <>
      {overlayContent}
      {openModals.map(
        ({ alertId, generationData, onClose: originalOnClose }) => {
          const liveAlert = activeAlerts.find((a) => a.id === alertId);
          const effectiveGenerationData =
            liveAlert?.generationData ?? generationData;

          const modalType =
            effectiveGenerationData?.modalType ||
            getCreativeJobAlertConfig(liveAlert?.jobType).modalType ||
            "createWithIA";

          if (modalType === "bulkCampaigns") {
            return (
              <ModalBulkCampaigns
                key={alertId}
                modalOpen={true}
                initialJobID={alertId}
                onCloseModal={() => onCloseModal(alertId, originalOnClose)}
              />
            );
          }

          if (modalType === "customReport") {
            return (
              <ModalCustomReport
                key={alertId}
                modalOpen={true}
                onCloseModal={() => onCloseModal(alertId, originalOnClose)}
                campaignID={effectiveGenerationData?.campaignID || ""}
                generationData={effectiveGenerationData}
                jobID={liveAlert?.jobID || alertId}
                alertStatus={liveAlert?.status}
                alertMessage={liveAlert?.message}
              />
            );
          }

          if (modalType === "imageEditLayers") {
            return (
              <ModalImageEdit
                key={alertId}
                isOpen={true}
                onClose={() => onCloseModal(alertId, originalOnClose)}
                initialImageUrl={
                  effectiveGenerationData?.sourceUrl ||
                  effectiveGenerationData?.url ||
                  null
                }
                initialWorkMode="layers"
                jobID={liveAlert?.jobID || alertId}
                alertStatus={liveAlert?.status}
                generationData={effectiveGenerationData}
                onCallbackSave={(payload) =>
                  onSaveImageEditFromAlert(payload, effectiveGenerationData)
                }
              />
            );
          }

          return (
            <AlertCreativeJobModals
              key={alertId}
              alertId={alertId}
              generationData={effectiveGenerationData}
              liveAlert={liveAlert}
              onClose={() => onCloseModal(alertId, originalOnClose)}
              onOpenGallery={onOpenGallery}
              userID={userID}
            />
          );
        }
      )}
    </>
  );
};

export default GlobalNotificationsPanel;
